import api from '@/api/api';
import { createQueryParams } from '@/utils/createQueryParams';

const DOMAIN = '/session';

const get = async (
	params: Sessions.SessionParams,
): Promise<Sessions.GetSessionsRespose> => {
	const queryParams = createQueryParams({ ...params });
	const { data } = await api.get(`${DOMAIN}?${queryParams}`);
	return data;
};

const getById = async (id: string): Promise<Sessions.Session> => {
	const { data } = await api.get(`${DOMAIN}/${id}`);
	return data;
};

const getVideoRoom = async (id: string): Promise<SessionCall.Credentials> => {
	const { data } = await api.get<{
		provider: Sessions.VideoProvider;
		roomUrl: string;
		token: string | null;
	}>(`${DOMAIN}/${id}/video-room`);
	return {
		url: data.roomUrl,
		token: data.token,
	};
};

const getMe = async (
	params: Sessions.SessionParams,
): Promise<Sessions.GetSessionsRespose> => {
	const queryParams = createQueryParams({ ...params });
	const { data } = await api.get(`${DOMAIN}/me?${queryParams}`);
	return data;
};

const create = async (body: Sessions.Create): Promise<Sessions.Session> => {
	const { data } = await api.post(`${DOMAIN}`, body);
	return data;
};

const patchPayment = async (
	paymentData: Sessions.PatchPayment,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${paymentData.id}/payment`,
		paymentData.body,
	);
	return data;
};

const updateManualPayment = async (
	paymentData: Sessions.UpdateManualPayment,
): Promise<Sessions.Session> => {
	const { data } = await api.put(
		`${DOMAIN}/${paymentData.sessionId}/pay`,
		paymentData.body,
	);
	return data;
};

const patchDate = async (
	dateData: Sessions.PatchDate,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${dateData.id}/date`,
		dateData.body,
	);
	return data;
};

const patchModality = async (
	modalityData: Sessions.PatchModality,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${modalityData.id}/modality`,
		modalityData.body,
	);
	return data;
};

const patchStatus = async (
	statusData: Sessions.PatchStatus,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${statusData.id}/status`,
		statusData.body,
	);
	return data;
};

const patchMetadata = async (
	metadataData: Sessions.PatchMetadata,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${metadataData.id}/metadata`,
		metadataData.body,
	);
	return data;
};

const patchDisplayName = async (
	displayNameData: Sessions.PatchDisplayName,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${displayNameData.id}/display-name`,
		displayNameData.body,
	);
	return data;
};

const patchEnd = async (
	id: string,
	body?: Sessions.EndSession,
): Promise<Sessions.Session> => {
	const rawNotes = body?.raw_notes?.trim();
	const { data } = await api.patch(
		`${DOMAIN}/${id}/end`,
		rawNotes ? { raw_notes: rawNotes } : undefined,
	);
	return data;
};

const patchStart = async (id: string): Promise<Sessions.Session> => {
	const { data } = await api.patch(`${DOMAIN}/${id}/start`);
	return data;
};

const getPosProcessingStatus = async (
	id: string,
): Promise<Sessions.PosProcessingStatus> => {
	const { data } = await api.get(`${DOMAIN}/${id}/is-pos-processing-done`);
	return data;
};

const reconcileVideoRoom = async (
	id: string,
): Promise<Sessions.ReconcileVideoRoomResponse> => {
	const { data } = await api.post<Sessions.ReconcileVideoRoomResponse>(
		`${DOMAIN}/${id}/video-room/reconcile`,
	);
	return data;
};

const patchParticipants = async (
	payload: Sessions.PatchParticipants,
): Promise<Sessions.Session> => {
	const { data } = await api.patch(
		`${DOMAIN}/${payload.id}/participants`,
		payload.body,
	);
	return data;
};

export type CancelSessionPayload = { id: string; reason: string };

/** Cancelamento por profissional/admin. Motivo obrigatório. */
const cancelAsProfessional = async (
	payload: CancelSessionPayload,
): Promise<{ message: string; session: Sessions.Session }> => {
	const { data } = await api.delete(`${DOMAIN}/${payload.id}`, {
		data: { reason: payload.reason },
	});
	return data;
};

export type CancelUserSessionResponse = {
	message: string;
	credit_restored: boolean;
	within_policy: boolean;
	session: Sessions.Session;
};

/** Cancelamento pelo próprio usuário (política de 24h aplicada no backend). */
const cancelAsUser = async (
	payload: CancelSessionPayload,
): Promise<CancelUserSessionResponse> => {
	const { data } = await api.delete(`${DOMAIN}/my/${payload.id}`, {
		data: { reason: payload.reason },
	});
	return data;
};

const pay = async (
	id: string,
	gateway: Sessions.PaymentGateway,
	user_club_subscription_benefit_id?: string,
): Promise<Sessions.PaySessionResponse> => {
	const body: {
		gateway: Sessions.PaymentGateway;
		user_club_subscription_benefit_id?: string;
	} = { gateway };
	if (user_club_subscription_benefit_id) {
		body.user_club_subscription_benefit_id =
			user_club_subscription_benefit_id;
	}
	const { data } = await api.post(`${DOMAIN}/${id}/pay`, body);
	return data;
};

// const getCheckoutUrl = async (sessionId: string): Promise<{ url: string }> => {
// 	const { data } = await api.get(`${DOMAIN}/${sessionId}/checkout-url`);
// 	return data;
// };

// const payWithAbacate = async (sessionId: string): Promise<{ url: string }> => {
// 	const { data } = await api.post(`${DOMAIN}/${sessionId}/pay-with-abacate`);
// 	return data;
// };

// const payWithCredit = async (
// 	sessionId: string,
// ): Promise<{ success: boolean; message: string }> => {
// 	const { data } = await api.post(`${DOMAIN}/${sessionId}/pay-with-credit`);
// 	return data;
// };

const SessionService = {
	get,
	getById,
	getVideoRoom,
	create,
	getMe,
	patchPayment,
	patchDate,
	patchModality,
	patchStatus,
	patchMetadata,
	patchDisplayName,
	patchEnd,
	patchStart,
	getPosProcessingStatus,
	reconcileVideoRoom,
	// getCheckoutUrl,
	patchParticipants,
	pay,
	cancelAsProfessional,
	cancelAsUser,
	updateManualPayment,
};

export { SessionService };
